import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { api, NO_STATUS, queryString, type FamiliesResult, type FamilyRow } from '../api/client.js';
import { DataTable } from '../components/DataTable.js';
import { AnalysisBanner } from '../components/AnalysisBanner.js';
import { Alert, Card, Input, PageHeader, Select, Spinner, StockNote } from '../components/ui.js';
import { day, decimal, integer } from '../format.js';
import { KEY_SOURCE_LABEL, familyPath } from '../lib/families.js';
import {
  compareInteger,
  compareIsoDate,
  compareNumericString,
  compareText,
  sortingFnFor,
} from '../lib/table-sort.js';

/**
 * Product families: every SKU grouped by its parent ASIN (or hand override),
 * with the restock figures rolled up. Purchase decisions are made per product
 * first, so this ranks the families the way Restock ranks the SKUs. Every
 * family comes down at once and the table sorts, filters, and pages on the
 * client; the CSV hands the same rows to a spreadsheet. See docs/analysis.md,
 * "Product families".
 */

const columnHelper = createColumnHelper<FamilyRow>();

function abbr(label: string, full: string): ReactNode {
  return <span title={full}>{label}</span>;
}

const BASIS_LABEL: Record<FamilyRow['scoreBasis'], string> = {
  in_stock_velocity: 'In-stock',
  active_months: 'Monthly',
  mixed: 'Mixed',
};

const COLUMNS = [
  columnHelper.accessor('name', {
    header: 'Family',
    sortingFn: sortingFnFor(compareText),
    cell: (c) => {
      const row = c.row.original;
      return (
        <span className="font-medium text-strong">
          {c.getValue() || row.key}
          {row.dataQuality.length > 0 && (
            <span
              className="ml-1 cursor-help text-amber-600 dark:text-amber-400"
              title={row.dataQuality.join('\n')}
              aria-label={`Data quality: ${row.dataQuality.join(', ')}`}
            >
              ⚠
            </span>
          )}
        </span>
      );
    },
  }),
  columnHelper.accessor('key', {
    header: 'Key',
    sortingFn: sortingFnFor(compareText),
    cell: (c) => (
      <span
        className="text-muted"
        title={`Family key from ${KEY_SOURCE_LABEL[c.row.original.keySource]}`}
      >
        {c.getValue()}
      </span>
    ),
  }),
  columnHelper.accessor('childCount', {
    header: () => abbr('Kids', 'Child SKUs in the family'),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareInteger),
    cell: (c) => integer(c.getValue()),
  }),
  columnHelper.accessor('activeChildren', {
    header: () => abbr('Act.', 'Active child SKUs'),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareInteger),
    cell: (c) => integer(c.getValue()),
  }),
  columnHelper.accessor('inStockChildren', {
    header: () => abbr('In stk', 'Child SKUs in stock'),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareInteger),
    cell: (c) => integer(c.getValue()),
  }),
  columnHelper.accessor((r) => r.stock ?? undefined, {
    id: 'stock',
    header: () => abbr('Qty', 'On-hand across the family'),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareInteger),
    cell: (c) => integer(c.getValue() ?? null),
  }),
  columnHelper.accessor('units30', {
    header: () => abbr('U30', 'Units sold, last 30 days'),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareInteger),
    cell: (c) => integer(c.getValue()),
  }),
  columnHelper.accessor('units90', {
    header: () => abbr('U90', 'Units sold, last 90 days'),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareInteger),
    cell: (c) => integer(c.getValue()),
  }),
  columnHelper.accessor('units365', {
    header: () => abbr('U365', 'Units sold, last 365 days'),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareInteger),
    cell: (c) => integer(c.getValue()),
  }),
  columnHelper.accessor('unitsLifetime', {
    header: () => abbr('Life', 'Units sold, lifetime'),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareInteger),
    cell: (c) => integer(c.getValue()),
  }),
  columnHelper.accessor((r) => r.unitsPer30InStock ?? undefined, {
    id: 'unitsPer30InStock',
    header: () =>
      abbr('U/30 IS', 'Units per 30 in-stock days, summed over in-stock-basis children'),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareNumericString),
    cell: (c) => {
      const row = c.row.original;
      return (
        <span
          title={`${row.unitsPer30InStockChildren} of ${row.childCount} children on in-stock basis`}
        >
          {decimal(c.getValue() ?? null, 2)}
        </span>
      );
    },
  }),
  columnHelper.accessor((r) => r.lastSaleDate ?? undefined, {
    id: 'lastSaleDate',
    header: () => abbr('Last sale', 'Newest last-sale day across the children'),
    sortingFn: sortingFnFor(compareIsoDate),
    cell: (c) => day(c.getValue()),
  }),
  columnHelper.accessor('sessionsSinceLastSale', {
    header: () => abbr('Sess. since', 'Sessions since the last sale, summed'),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareInteger),
    cell: (c) => integer(c.getValue()),
  }),
  columnHelper.accessor('estLostUnits', {
    header: () => abbr('Est. lost', 'Estimated lost units while out of stock, summed'),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareNumericString),
    cell: (c) => decimal(c.getValue(), 0),
  }),
  columnHelper.accessor('stockoutDays365Max', {
    header: () =>
      abbr('OOS 365', 'Out-of-stock days over the last 365 (worst child; mean in tooltip)'),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareInteger),
    cell: (c) => (
      <span title={`Mean ${decimal(c.row.original.stockoutDays365Mean, 2)} across children`}>
        {integer(c.getValue())}
      </span>
    ),
  }),
  columnHelper.accessor('score', {
    header: () => abbr('Score', 'Restock score, summed over the children; higher is stronger'),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareNumericString),
    cell: (c) => <span className="font-medium text-strong">{decimal(c.getValue(), 2)}</span>,
  }),
  columnHelper.accessor('scoreBasis', {
    header: 'Basis',
    sortingFn: sortingFnFor(compareText),
    cell: (c) => BASIS_LABEL[c.getValue()],
  }),
];

export function FamiliesPage({ heading = true }: { heading?: boolean }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [basis, setBasis] = useState('');
  const [search, setSearch] = useState('');

  const query = useQuery({
    queryKey: ['families'],
    queryFn: () => api.get<FamiliesResult>('/api/families'),
  });

  // One option per status present among the families (a family shows under a
  // status when any child has it), plus a "(no status)" bucket, each with the
  // number of families; nulls last.
  const statusOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const family of query.data?.families ?? []) {
      const seen = new Set(family.statusCounts.filter((s) => s.count > 0).map((s) => s.status));
      for (const s of seen) counts.set(s, (counts.get(s) ?? 0) + 1);
    }
    return [...counts.entries()].sort(([a], [b]) => {
      if (a === NO_STATUS) return 1;
      if (b === NO_STATUS) return -1;
      return a.localeCompare(b);
    });
  }, [query.data]);

  const basisOptions = useMemo(() => {
    let inStock = 0;
    let monthly = 0;
    for (const family of query.data?.families ?? []) {
      if (family.hasInStockBasis) inStock += 1;
      if (family.hasActiveMonthsBasis) monthly += 1;
    }
    return { inStock, monthly };
  }, [query.data]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (query.data?.families ?? [])
      .filter((f) =>
        !status ? true : (f.statusCounts.find((s) => s.status === status)?.count ?? 0) > 0,
      )
      .filter((f) =>
        !basis ? true : basis === 'in_stock_velocity' ? f.hasInStockBasis : f.hasActiveMonthsBasis,
      )
      .filter((f) => (!q ? true : `${f.key} ${f.name} ${f.searchText}`.toLowerCase().includes(q)));
  }, [query.data, status, basis, search]);

  const csvHref = `/api/families.csv${queryString({ status, basis, q: search.trim() })}`;

  return (
    <div className="space-y-4">
      {heading && (
        <PageHeader
          title="Families"
          description="Every SKU grouped into the product it is a size or color of, with the restock figures rolled up. Decide whether a product is worth restocking here, then which sizes on its page. See docs/analysis.md for the family key and the columns."
        />
      )}

      <AnalysisBanner />

      {query.isPending && <Spinner label="Rolling up families" />}
      {query.isError && <Alert>{query.error.message}</Alert>}
      {query.data && (
        <Card>
          <DataTable
            data={rows}
            columns={COLUMNS}
            initialSorting={[{ id: 'score', desc: true }]}
            storageKey="families"
            resetKey={`${status}|${basis}|${search.trim()}`}
            dense
            stickyFirstColumn
            onRowClick={(row) => void navigate(familyPath(row.key))}
            emptyText="No families yet. Import an All Listings report and a Business Report by child ASIN."
            toolbar={
              <>
                <span className="text-xs text-muted">
                  {integer(rows.length)} famil{rows.length === 1 ? 'y' : 'ies'}
                </span>
                <div className="w-40">
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search"
                    aria-label="Search families"
                  />
                </div>
                <div className="w-44">
                  <Select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    aria-label="Status"
                  >
                    <option value="">Any status</option>
                    {statusOptions.map(([s, count]) => (
                      <option key={s} value={s}>
                        {s === NO_STATUS ? '(no status)' : s} ({integer(count)})
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="w-44">
                  <Select
                    value={basis}
                    onChange={(e) => setBasis(e.target.value)}
                    aria-label="Basis"
                  >
                    <option value="">Any basis</option>
                    <option value="in_stock_velocity">
                      In-stock ({integer(basisOptions.inStock)})
                    </option>
                    <option value="active_months">Monthly ({integer(basisOptions.monthly)})</option>
                  </Select>
                </div>
                <StockNote source={query.data.stock.source} asOf={query.data.stock.asOf} />
                <a
                  href={csvHref}
                  className="inline-flex items-center rounded-md bg-surface px-3 py-2 text-sm font-medium text-body ring-1 ring-inset ring-line-strong hover:bg-sunken"
                >
                  Download CSV
                </a>
              </>
            }
          />
        </Card>
      )}
    </div>
  );
}
